// src/utils/formazioneCalendario.js — copia identica di functions/formazioneCalendario.js
// (le functions non possono importare da src; cambia solo l'ultima riga, export/module.exports).
// La prova demo/prove/prova_calendario_formazione.mjs controlla che diano lo stesso risultato.
// ═══════════════════════════════════════════════════════════════════════════
// CALENDARIO DELLA FORMAZIONE (ottobre 2026, W427, regole 68)
// ═══════════════════════════════════════════════════════════════════════════
// Il Platform Owner inserisce le FASCE libere (formazioneFasce: data, dalle, alle, modalità).
// Titolare e amministratori di un'azienda prenotano un orario dentro una fascia: l'orario
// deve stare tutto nella fascia, con 15 minuti di pausa dagli altri incontri, e almeno 24 ore
// prima. La fascia tiene solo gli orari occupati (occupati: [{ inizio, fine, prenotazioneId }]),
// senza dati dei clienti: la leggono tutti per mostrare gli orari liberi.
// Regole decise con Paolo:
//  • l'avvio gratuito una sola volta per AZIENDA (formazioneAvvii/{orgId}), anche se lo chiede
//    un collega; se viene annullato torna disponibile;
//  • le formule a pagamento si possono prenotare più volte, con l'avviso «un collega ha già…»;
//  • avvio gratuito confermato subito; formule a pagamento «da confermare» dal Platform Owner
//    (si paga solo dopo la data confermata);
//  • il cliente annulla da solo fino a 48 ore prima; dopo scrive (il Platform Owner sempre).
//  • W428: DEMO GRATUITA IN VIDEO CALL (20 minuti) per chi non è ancora cliente, prenotata dal
//    sito senza account: Partita IVA valida e obbligatoria, una demo per Partita IVA ed email,
//    al massimo N a settimana (impostazione del Platform Owner, 3 se non indicata), sempre da
//    confermare; settimana piena o nessuna data → «lasciaci il numero, ti richiamiamo».
// Tutte le date e gli orari sono «di orologio» italiani (Europe/Rome), come stringhe.
// ═══════════════════════════════════════════════════════════════════════════
const REGOLE_CALENDARIO = { passoMinuti: 30, pausaMinuti: 15, anticipoOre: 24, oreAnnullamento: 48, avvioMinuti: 45, avvioEntroGiorniProva: 30,
  demoPerSettimana: 3, demoMinuti: 20, demoAlGiorno: 10, demoSecondiMinimi: 5 };
const DURATE = { avvio: 45, ora: 60, videocall: 180, presenza: 180, demo: 20 };
const NOMI_FORMULE = { avvio: "Avvio gratuito", videocall: "Pacchetto in video call (3 ore)", presenza: "Pacchetto in presenza (3 ore)", ora: "Ora singola in video call", demo: "Demo gratuita in video call (20 minuti)" };
const MODALITA = { videocall: "Solo video call", presenza: "Solo in presenza", entrambe: "Video call o in presenza" };
const A_PAGAMENTO = ["videocall", "presenza", "ora"];

const pad2 = (n) => String(n).padStart(2, "0");
const minuti = (hhmm) => { const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "")); return m ? Number(m[1]) * 60 + Number(m[2]) : NaN; };
const orario = (n) => `${pad2(Math.floor(n / 60))}:${pad2(n % 60)}`;
const giornoUtc = (s) => { const [a, m, g] = String(s).split("-").map(Number); return Date.UTC(a, m - 1, g); };
const DATA_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Adesso, con l'orologio italiano: { data: "2026-10-24", minuti: 630 }. */
function adessoRoma(d = new Date()) {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(d).map(x => [x.type, x.value]));
  return { data: `${p.year}-${p.month}-${p.day}`, minuti: Number(p.hour) * 60 + Number(p.minute) };
}
/** Minuti che mancano dall'adesso all'inizio indicato (negativi se è già passato). */
function minutiAlInizio(data, inizio, adesso) {
  return (giornoUtc(data) - giornoUtc(adesso.data)) / 60000 + minuti(inizio) - adesso.minuti;
}
/** Fine dell'incontro "HH:MM". */
const fineIncontro = (inizio, formula) => orario(minuti(inizio) + (DURATE[formula] || 0));

/** La fascia accetta la formula? (presenza solo nelle fasce «in presenza» o «entrambe») */
function formulaAmmessa(fascia, formula) {
  const m = (fascia && fascia.modalita) || "videocall";
  if (!DURATE[formula]) return false;
  return formula === "presenza" ? m !== "videocall" : m !== "presenza";
}

/** Fascia valida? (per l'inserimento del Platform Owner) → testo dell'errore o "" */
function erroreFascia(f, adesso) {
  if (!f || !DATA_RE.test(f.data || "")) return "Indica la data.";
  const a = minuti(f.inizio), b = minuti(f.fine);
  if (Number.isNaN(a) || Number.isNaN(b)) return "Indica l'orario di inizio e di fine.";
  if (b - a < REGOLE_CALENDARIO.avvioMinuti) return `La fascia deve durare almeno ${REGOLE_CALENDARIO.avvioMinuti} minuti.`;
  if (!MODALITA[f.modalita]) return "Scegli la modalità.";
  if (adesso && f.data < adesso.data) return "La data è già passata.";
  return "";
}

/** Orari d'inizio liberi in una fascia per la formula ("HH:MM"). */
function orariLiberi(fascia, formula, adesso) {
  if (!fascia || !formulaAmmessa(fascia, formula)) return [];
  const dur = DURATE[formula], P = REGOLE_CALENDARIO.pausaMinuti;
  const ini = minuti(fascia.inizio), fin = minuti(fascia.fine);
  if (Number.isNaN(ini) || Number.isNaN(fin)) return [];
  const occ = (fascia.occupati || []).map(o => [minuti(o.inizio) - P, minuti(o.fine) + P]);
  const out = [];
  for (let t = ini; t + dur <= fin; t += REGOLE_CALENDARIO.passoMinuti) {
    if (occ.some(([a, b]) => t < b && t + dur > a)) continue;
    if (adesso && minutiAlInizio(fascia.data, orario(t), adesso) < REGOLE_CALENDARIO.anticipoOre * 60) continue;
    out.push(orario(t));
  }
  return out;
}

/** Fasce con almeno un orario libero per la formula, in ordine di data: [{ fascia, orari }]. */
function fasceDisponibili(fasce, formula, adesso) {
  return (fasce || [])
    .map(f => ({ fascia: f, orari: orariLiberi(f, formula, adesso) }))
    .filter(x => x.orari.length > 0)
    .sort((x, y) => (x.fascia.data + x.fascia.inizio).localeCompare(y.fascia.data + y.fascia.inizio));
}

const aDate = (v) => (v && typeof v.toDate === "function" ? v.toDate() : (v ? new Date(v) : null));
/** Avvio gratuito ancora possibile per l'azienda? (prova in corso, entro i primi 30 giorni) */
function avvioNelPeriodo(org, adesso = new Date()) {
  const piano = org && org.plan;
  const pagato = !!piano && piano !== "free" && org.status === "active";
  const fine = aDate(org && org.trialEndsAt);
  const inProva = !pagato && (!fine || fine > adesso);
  if (!inProva) return false;
  const nata = aDate(org && org.createdAt);
  return !nata || (adesso - nata) / 86400000 <= REGOLE_CALENDARIO.avvioEntroGiorniProva;
}

const dataBreve = (s) => { const [a, m, g] = String(s || "").split("-"); return a ? `${g}/${m}/${a}` : ""; };
const dataDi = (v) => { const d = aDate(v); return d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : ""; };

/**
 * Formazioni a pagamento già fatte, prenotate o chieste da COLLEGHI della stessa azienda
 * (per l'avviso «un collega ha già…»). Escluse quelle annullate e quelle di chi prenota.
 * → [{ nome, formula, data, tipo: "fatta" | "prenotata" | "richiesta" }]
 */
function precedentiAzienda({ prenotazioni = [], richieste = [], uid, oggi }) {
  const out = [];
  prenotazioni.forEach(p => {
    if (!p || p.uid === uid || p.stato === "annullata" || !A_PAGAMENTO.includes(p.formula)) return;
    out.push({ nome: p.nome || "Un collega", formula: p.formula, data: p.data, tipo: p.data < oggi ? "fatta" : "prenotata" });
  });
  richieste.forEach(r => {
    if (!r || r.uid === uid || r.stato === "annullata" || !A_PAGAMENTO.includes(r.formula)) return;
    out.push({ nome: r.nome || "Un collega", formula: r.formula, data: dataDi(r.createdAt), tipo: "richiesta" });
  });
  return out.sort((a, b) => String(b.data).localeCompare(String(a.data)));
}
function testoPrecedente(p) {
  const f = `«${NOMI_FORMULE[p.formula] || p.formula}»`;
  if (p.tipo === "fatta") return `${p.nome} ha già fatto la formazione ${f} il ${dataBreve(p.data)}.`;
  if (p.tipo === "prenotata") return `${p.nome} ha già prenotato la formazione ${f} per il ${dataBreve(p.data)}.`;
  return `${p.nome} ha già chiesto la formazione ${f}${p.data ? ` il ${dataBreve(p.data)}` : ""}.`;
}
/** Testo dell'avviso quando l'avvio dell'azienda è già preso. */
function testoAvvioUsato(a) {
  if (!a) return "";
  if (a.data) return `L'avvio gratuito per la vostra azienda è già stato prenotato da ${a.nome || "un collega"} per il ${dataBreve(a.data)}.`;
  return `L'avvio gratuito per la vostra azienda è già stato chiesto da ${a.nome || "un collega"}${a.chiestoIl ? ` il ${dataBreve(a.chiestoIl)}` : ""}.`;
}

const MESSAGGI = {
  non_autorizzato: "La formazione la prenotano il titolare o un amministratore dell'azienda.",
  formula: "Scegli la formula.",
  avvio_periodo: `L'avvio gratuito si chiede durante la prova, entro i primi ${REGOLE_CALENDARIO.avvioEntroGiorniProva} giorni.`,
  avvio_usato: "L'avvio gratuito per la vostra azienda è già stato prenotato o chiesto da un collega.",
  fascia: "Questa data non è più disponibile: scegline un'altra.",
  orario_occupato: "Questo orario è appena stato preso: scegline un altro.",
  sede: "Per la formazione in presenza indica l'indirizzo della sede.",
  date: "Indica qualche data o orario possibile.",
};
const errore = (codice, extra = {}) => ({ errore: codice, messaggio: MESSAGGI[codice] || "Prenotazione non riuscita.", ...extra });
const testo = (v, max) => String(v == null ? "" : v).trim().slice(0, max);

/**
 * Prenotazione (o richiesta senza data per l'avvio): solo controlli e dati, niente database.
 * La Cloud Function la chiama dentro una transazione con la fascia e l'avvio dell'azienda letti
 * in quel momento, così due aziende non prendono lo stesso orario e l'avvio resta uno.
 * @param p { formula, fasciaId, fascia (dati letti o null), inizio, avvio (formazioneAvvii/{orgId} o null),
 *   org, orgId, utente: { uid, ruolo, nome, email }, dati: { partecipanti, oreAggiuntive, sede, telefono, note, date },
 *   adesso: Date, idPrenotazione, idRichiesta, precedenti, link }
 * @returns { errore, messaggio } oppure { prenotazione | richiesta, occupati, avvio, stato }
 */
function eseguiPrenotazione(p) {
  const { formula, fasciaId, fascia, inizio, avvio, org, orgId, utente = {}, dati = {}, idPrenotazione, idRichiesta, precedenti = [], link = "" } = p;
  const adessoD = p.adesso || new Date(), adesso = adessoRoma(adessoD);
  if (!orgId || !["titolare", "admin"].includes(utente.ruolo)) return errore("non_autorizzato");
  if (!DURATE[formula]) return errore("formula");
  if (formula === "avvio") {
    if (!avvioNelPeriodo(org, adessoD)) return errore("avvio_periodo");
    if (avvio) return errore("avvio_usato", { messaggio: testoAvvioUsato(avvio) });
  }
  const sede = formula === "presenza" ? testo(dati.sede, 200) : "";
  if (formula === "presenza" && sede.length < 5) return errore("sede");
  const comuni = {
    orgId, orgNome: testo((org && (org.name || org.nome)) || "", 200), uid: utente.uid, nome: testo(utente.nome, 120), email: testo(utente.email, 200),
    telefono: testo(dati.telefono, 40), formula, partecipanti: Math.max(1, Math.min(50, Number(dati.partecipanti) || 1)),
    oreAggiuntive: formula === "videocall" || formula === "presenza" ? Math.max(0, Math.min(20, Number(dati.oreAggiuntive) || 0)) : 0,
    sede, note: testo(dati.note, 1000),
  };
  const avvioDoc = (extra) => (formula === "avvio" ? { orgId, orgNome: comuni.orgNome, uid: utente.uid, nome: comuni.nome, ...extra } : null);
  if (!fasciaId) {
    // Nessuna data adatta: richiesta libera. Dalla funzione passa solo l'avvio (per tenerlo
    // unico per azienda); le altre formule le scrive l'app, come prima.
    const date = testo(dati.date, 300);
    if (date.length < 3) return errore("date");
    return { stato: "nuova", richiesta: { ...comuni, date, utentiAttivi: Number(org && org.seatsUsed) || 0, stato: "nuova" },
      avvio: avvioDoc({ richiestaId: idRichiesta, chiestoIl: adesso.data }) };
  }
  if (!fascia) return errore("fascia");
  if (!orariLiberi(fascia, formula, adesso).includes(inizio)) return errore(formulaAmmessa(fascia, formula) ? "orario_occupato" : "fascia");
  const fine = fineIncontro(inizio, formula);
  const stato = formula === "avvio" ? "confermata" : "da_confermare";
  return {
    stato,
    prenotazione: { ...comuni, fasciaId, data: fascia.data, inizio, fine, stato, precedenti: precedenti.slice(0, 5).map(testoPrecedente),
      link: formula === "presenza" ? "" : testo(stato === "confermata" ? link : "", 500) },
    occupati: [...(fascia.occupati || []), { inizio, fine, prenotazioneId: idPrenotazione }],
    avvio: avvioDoc({ prenotazioneId: idPrenotazione, data: fascia.data, inizio }),
  };
}

/**
 * Conferma o annullamento di una prenotazione.
 * @param p { azione: "conferma" | "annulla", prenotazione, idPrenotazione, fascia, avvio, chi: { uid, ruolo, orgId, isPO }, adesso: Date, link, motivo }
 * @returns { errore, messaggio } oppure { aggiorna, occupati (o null), liberaAvvio }
 */
function eseguiAggiornamento(p) {
  const { azione, prenotazione: pr, idPrenotazione, fascia, avvio, chi = {}, link, motivo } = p;
  const adesso = adessoRoma(p.adesso || new Date());
  if (!pr) return { errore: "non_trovata", messaggio: "Prenotazione non trovata." };
  const dellAzienda = chi.orgId === pr.orgId && ["titolare", "admin"].includes(chi.ruolo);
  if (pr.stato === "annullata") return { errore: "annullata", messaggio: "La prenotazione è già annullata." };
  if (azione === "conferma") {
    if (!chi.isPO) return { errore: "non_autorizzato", messaggio: "Solo Nexiora conferma le prenotazioni." };
    return { aggiorna: { stato: "confermata", link: pr.formula === "presenza" ? "" : testo(link, 500) }, occupati: null, liberaAvvio: false };
  }
  if (azione !== "annulla") return { errore: "azione", messaggio: "Operazione non riconosciuta." };
  if (!chi.isPO && !dellAzienda) return { errore: "non_autorizzato", messaggio: MESSAGGI.non_autorizzato };
  if (!chi.isPO && minutiAlInizio(pr.data, pr.inizio, adesso) < REGOLE_CALENDARIO.oreAnnullamento * 60) {
    return { errore: "troppo_tardi", messaggio: `Mancano meno di ${REGOLE_CALENDARIO.oreAnnullamento} ore: per annullare o spostare scrivi a info@nexiora.it.` };
  }
  return {
    aggiorna: { stato: "annullata", annullataDa: chi.isPO ? "nexiora" : "cliente", motivo: testo(motivo, 300) },
    occupati: fascia ? (fascia.occupati || []).filter(o => o.prenotazioneId !== idPrenotazione) : null,
    liberaAvvio: pr.formula === "avvio" && !!avvio && avvio.prenotazioneId === idPrenotazione,
  };
}

// ── DEMO DAL SITO (W428) ────────────────────────────────────────────────────
/** Lunedì della settimana di una data "AAAA-MM-GG" (stessa stringa). */
function lunediDiData(data) {
  const [a, m, g] = String(data).split("-").map(Number);
  const d = new Date(Date.UTC(a, m - 1, g));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}
/** Partita IVA italiana valida (11 cifre con la cifra di controllo); accetta spazi e «IT». */
function normalizzaPivaDemo(v) { return String(v || "").toUpperCase().replace(/\s+/g, "").replace(/^IT/, ""); }
function pivaValidaDemo(v) {
  const p = normalizzaPivaDemo(v);
  if (!/^\d{11}$/.test(p) || /^0{11}$/.test(p)) return false;
  let somma = 0;
  for (let i = 0; i < 10; i++) { let c = Number(p[i]); if (i % 2 === 1) { c *= 2; if (c > 9) c -= 9; } somma += c; }
  return (10 - (somma % 10)) % 10 === Number(p[10]);
}
const maxDemo = (n) => (n === null || n === undefined || n === "" || Number.isNaN(Number(n)) ? REGOLE_CALENDARIO.demoPerSettimana : Math.max(0, Math.min(50, Math.floor(Number(n)))));   // 0 = solo «ti richiamiamo»
/** Demo attive (non annullate) per settimana: { "2026-11-02": 2 } */
function demoPerSettimana(demo) {
  const out = {};
  (demo || []).forEach(p => { if (p && p.stato !== "annullata" && p.data) { const k = lunediDiData(p.data); out[k] = (out[k] || 0) + 1; } });
  return out;
}
/** Giorni e orari prenotabili per la demo: [{ fasciaId, data, orari }] (settimane piene escluse). */
function demoDisponibili({ fasce = [], demo = [], max, adesso }) {
  const piene = demoPerSettimana(demo), lim = maxDemo(max);
  return fasceDisponibili(fasce, "demo", adesso)
    .filter(x => (piene[lunediDiData(x.fascia.data)] || 0) < lim)
    .map(x => ({ fasciaId: x.fascia.id, data: x.fascia.data, orari: x.orari }));
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MESSAGGI_DEMO = {
  robot: "Prenotazione non riuscita. Riprova tra qualche secondo.",
  azienda: "Indica il nome dell'azienda.",
  piva: "La Partita IVA non è valida: controlla le 11 cifre.",
  nome: "Indica nome e cognome.",
  email: "L'indirizzo email non è valido.",
  telefono: "Indica un numero di telefono.",
  privacy: "Per prenotare serve la presa visione dell'informativa privacy.",
  troppe: "Oggi abbiamo ricevuto molte richieste: lasciaci il numero, ti richiamiamo noi.",
  gia_prenotata: "Per questa azienda (o questo indirizzo email) c'è già una demo prenotata o richiesta: ti contattiamo noi. Per cambiare data rispondi all'email che hai ricevuto.",
  settimana_piena: "Le demo di questa settimana sono al completo: scegli un'altra settimana oppure lasciaci il numero.",
  orario_occupato: "Questo orario è appena stato preso: scegline un altro.",
  fascia: "Questa data non è più disponibile: scegline un'altra.",
};
const erroreDemo = (codice) => ({ errore: codice, messaggio: MESSAGGI_DEMO[codice] || "Prenotazione non riuscita." });

/**
 * Demo prenotata dal sito (o richiesta di essere richiamati, senza fasciaId).
 * @param p { fasciaId, fascia (con id), inizio, dati: { azienda, piva, settore, persone, nome, email, telefono, note, quando, privacy, sito (campo
 *   trappola, deve restare vuoto), secondi (tempo di compilazione) }, demo (prenotazioni demo esistenti), richieste (richieste demo esistenti),
 *   max, giaOggi (demo e richieste arrivate oggi dal sito), adesso: Date, idPrenotazione, idRichiesta }
 */
function eseguiDemo(p) {
  const { fasciaId, fascia, inizio, dati = {}, demo = [], richieste = [], max, giaOggi = 0, idPrenotazione } = p;
  const adesso = adessoRoma(p.adesso || new Date());
  if (testo(dati.sito, 200) || Number(dati.secondi) < REGOLE_CALENDARIO.demoSecondiMinimi) return erroreDemo("robot");
  const azienda = testo(dati.azienda, 160), piva = normalizzaPivaDemo(dati.piva), nome = testo(dati.nome, 120);
  const email = testo(dati.email, 200).toLowerCase(), telefono = testo(dati.telefono, 40);
  if (azienda.length < 2) return erroreDemo("azienda");
  if (!pivaValidaDemo(piva)) return erroreDemo("piva");
  if (nome.length < 3) return erroreDemo("nome");
  if (!EMAIL_RE.test(email)) return erroreDemo("email");
  if (telefono.replace(/\D/g, "").length < 6) return erroreDemo("telefono");
  if (dati.privacy !== true) return erroreDemo("privacy");
  if (giaOggi >= REGOLE_CALENDARIO.demoAlGiorno) return erroreDemo("troppe");
  const stessa = (x) => x && x.stato !== "annullata" && x.stato !== "gestita" && (x.piva === piva || String(x.email || "").toLowerCase() === email);
  if (demo.some(stessa) || richieste.some(stessa)) return erroreDemo("gia_prenotata");
  const comuni = { orgId: "", orgNome: azienda, piva, settore: testo(dati.settore, 80), persone: testo(dati.persone, 20), uid: "", nome, email, telefono,
    formula: "demo", partecipanti: 1, oreAggiuntive: 0, sede: "", note: testo(dati.note, 600), origine: "sito" };
  if (!fasciaId) {
    return { stato: "nuova", richiesta: { ...comuni, date: testo(dati.quando, 300) || "Da concordare per telefono", utentiAttivi: 0, stato: "nuova" } };
  }
  if (!fascia) return erroreDemo("fascia");
  if ((demoPerSettimana(demo)[lunediDiData(fascia.data)] || 0) >= maxDemo(max)) return erroreDemo("settimana_piena");
  if (!orariLiberi(fascia, "demo", adesso).includes(inizio)) return erroreDemo(formulaAmmessa(fascia, "demo") ? "orario_occupato" : "fascia");
  const fine = fineIncontro(inizio, "demo");
  return {
    stato: "da_confermare",
    prenotazione: { ...comuni, fasciaId, data: fascia.data, inizio, fine, stato: "da_confermare", precedenti: [], link: "" },
    occupati: [...(fascia.occupati || []), { inizio, fine, prenotazioneId: idPrenotazione }],
  };
}

/** Invito da aggiungere al calendario (.ics), con il fuso di Roma. */
function invitoIcs({ id, data, inizio, fine, titolo, descrizione = "", luogo = "", annullato = false }) {
  const esc = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
  const quando = (h) => `${String(data).replace(/-/g, "")}T${String(h).replace(":", "")}00`;
  const ora = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");
  return ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Nexiora//Formazione//IT", "CALSCALE:GREGORIAN", `METHOD:${annullato ? "CANCEL" : "PUBLISH"}`,
    "BEGIN:VTIMEZONE", "TZID:Europe/Rome",
    "BEGIN:DAYLIGHT", "TZOFFSETFROM:+0100", "TZOFFSETTO:+0200", "TZNAME:CEST", "DTSTART:19700329T020000", "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU", "END:DAYLIGHT",
    "BEGIN:STANDARD", "TZOFFSETFROM:+0200", "TZOFFSETTO:+0100", "TZNAME:CET", "DTSTART:19701025T030000", "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU", "END:STANDARD",
    "END:VTIMEZONE",
    "BEGIN:VEVENT", `UID:formazione-${id}@nexiora.it`, `DTSTAMP:${ora}`, `DTSTART;TZID=Europe/Rome:${quando(inizio)}`, `DTEND;TZID=Europe/Rome:${quando(fine)}`,
    `SUMMARY:${esc(titolo)}`, `DESCRIPTION:${esc(descrizione)}`, ...(luogo ? [`LOCATION:${esc(luogo)}`] : []), `STATUS:${annullato ? "CANCELLED" : "CONFIRMED"}`,
    "END:VEVENT", "END:VCALENDAR"].join("\r\n");
}

export { REGOLE_CALENDARIO, DURATE, NOMI_FORMULE, MODALITA, A_PAGAMENTO, adessoRoma, minutiAlInizio, fineIncontro, formulaAmmessa, erroreFascia, orariLiberi, fasceDisponibili, avvioNelPeriodo, precedentiAzienda, testoPrecedente, testoAvvioUsato, eseguiPrenotazione, eseguiAggiornamento, invitoIcs, dataBreve, lunediDiData, pivaValidaDemo, normalizzaPivaDemo, demoDisponibili, demoPerSettimana, eseguiDemo, MESSAGGI_DEMO };
